import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { BRAND } from "@/lib/brand";
import { Menu, X, ChevronDown } from "lucide-react";

function MobileSection({ title, children }: { title: string; children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-2 text-white/90 font-medium"
      >
        {title}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="pl-4 pb-2 space-y-1">{children}</div>}
    </div>
  );
}

export function SiteHeader({ lang = 'en', onToggleLang }: { lang?: 'en' | 'fr'; onToggleLang?: () => void; }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  const navLinks = {
    services: [
      { to: "/landing/sellers", label: lang === 'fr' ? 'Pour vendeurs' : 'For Sellers' },
      { to: "/landing/buyers", label: lang === 'fr' ? 'Pour acheteurs' : 'For Buyers' },
      { to: "/landing/consultants", label: lang === 'fr' ? 'Pour consultants' : 'For Consultants' },
      { to: "/landing/brokers", label: lang === 'fr' ? 'Pour courtiers' : 'For Brokers' },
    ],
    resources: [
      { to: "/listings", label: lang === 'fr' ? 'À vendre' : 'For Sale' },
      { to: "/blog", label: 'Blog' },
      { to: "/resources/e-learning", label: 'E-learning' },
    ],
    partners: [
      { to: "/landing/brokers", label: lang === 'fr' ? 'Courtiers' : 'Brokers' },
      { to: "/landing/lenders", label: lang === 'fr' ? 'Prêteurs' : 'Lenders' },
      { to: "/landing/partners", label: lang === 'fr' ? 'Avocats en droit des affaires' : 'Business Lawyers' },
      { to: "/landing/partners", label: lang === 'fr' ? "Avocats en immigration" : 'Immigration Lawyers' },
      { to: "/landing/partners", label: lang === 'fr' ? 'Notaires' : 'Notaries' },
      { to: "/landing/partners", label: 'CPA' },
      { to: "/landing/partners", label: lang === 'fr' ? 'Conseillers fiscaux' : 'Tax Advisors' },
    ],
    portal: [
      { to: "/login?next=/admin-dashboard", label: 'Admin Portal' },
      { to: "/login?next=/partners/dashboard", label: 'Business Partners Portal' },
      { to: "/login?next=/staff-dashboard", label: 'Staff Portal' },
    ],
  };

  return (
    <header className="w-full" style={{ backgroundColor: BRAND.navy }}>
      <div className="mx-auto max-w-7xl px-4 py-5 flex items-center justify-between">
        {/* Logo */}
        <div className="flex items-center header-logo-wrap">
          <Link to="/">
            <img src="/noBgColor.png" alt="AcquizGroup" className="ag-logo-header" />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 text-sm text-white/90">
          {/* Services */}
          <div className="group relative">
            <span className="cursor-default hover:opacity-90">{lang === 'fr' ? 'Services' : 'Services'}</span>
            <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
              <div className="rounded-xl border bg-white text-gray-900 shadow-lg">
                {navLinks.services.map(l => (
                  <Link key={l.to} to={l.to} className="block px-4 py-2 hover:bg-gray-50">{l.label}</Link>
                ))}
              </div>
            </div>
          </div>

          {/* Resources */}
          <div className="group relative">
            <span className="cursor-default">{lang === 'fr' ? 'Ressources' : 'Resources'}</span>
            <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
              <div className="rounded-xl border bg-white text-gray-900 shadow-lg">
                {navLinks.resources.map(l => (
                  <Link key={l.to} to={l.to} className="block px-4 py-2 hover:bg-gray-50">{l.label}</Link>
                ))}
              </div>
            </div>
          </div>

          <NavLink to="/about" className="hover:opacity-90">{lang === 'fr' ? 'À propos' : 'About'}</NavLink>

          {/* Our Partners */}
          <div className="group relative">
            <span className="cursor-default">{lang === 'fr' ? 'Nos experts' : 'Our Experts'}</span>
            <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
              <div className="rounded-xl border bg-white text-gray-900 shadow-lg">
                {navLinks.partners.map(l => (
                  <Link key={l.to + l.label} to={l.to} className="block px-4 py-2 hover:bg-gray-50">{l.label}</Link>
                ))}
              </div>
            </div>
          </div>

          {/* Portal */}
          <div className="group relative">
            <span className="cursor-default">Portal</span>
            <div className="absolute left-0 top-full pt-2 hidden group-hover:block z-50">
              <div className="rounded-xl border bg-white text-gray-900 shadow-lg">
                {navLinks.portal.map(l => (
                  <Link key={l.to} to={l.to} className="block px-4 py-2 hover:bg-gray-50">{l.label}</Link>
                ))}
              </div>
            </div>
          </div>
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-2">
          <button onClick={onToggleLang} className="rounded-md bg-white text-gray-900 text-xs px-2 py-1 font-semibold">
            {lang === 'fr' ? 'EN' : 'FR'}
          </button>
          <Link to="/contact" className="hidden sm:inline-block rounded-md text-sm font-semibold px-3 py-1 text-white hover:bg-white/10">
            Contact
          </Link>
          <Link to="/login" className="hidden sm:inline-block rounded-md text-sm font-semibold px-3 py-1"
            style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})`, color: '#1f2937' }}>
            {lang === 'fr' ? 'Se connecter' : 'Sign In'}
          </Link>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-white hover:bg-white/10 rounded-lg"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-white/10 px-4 pb-6 pt-2 space-y-1 text-sm" style={{ backgroundColor: BRAND.navy }}>
          <MobileSection title={lang === 'fr' ? 'Services' : 'Services'}>
            {navLinks.services.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block py-1.5 text-white/70 hover:text-white">{l.label}</Link>
            ))}
          </MobileSection>

          <MobileSection title={lang === 'fr' ? 'Ressources' : 'Resources'}>
            {navLinks.resources.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block py-1.5 text-white/70 hover:text-white">{l.label}</Link>
            ))}
          </MobileSection>

          <Link to="/about" onClick={() => setMobileOpen(false)} className="block py-2 text-white/90 font-medium hover:text-white">
            {lang === 'fr' ? 'À propos' : 'About'}
          </Link>

          <MobileSection title={lang === 'fr' ? 'Nos experts' : 'Our Experts'}>
            {navLinks.partners.map(l => (
              <Link key={l.to + l.label} to={l.to} onClick={() => setMobileOpen(false)} className="block py-1.5 text-white/70 hover:text-white">{l.label}</Link>
            ))}
          </MobileSection>

          <MobileSection title="Portal">
            {navLinks.portal.map(l => (
              <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="block py-1.5 text-white/70 hover:text-white">{l.label}</Link>
            ))}
          </MobileSection>

          <hr className="border-white/10 my-3" />

          <Link to="/contact" onClick={() => setMobileOpen(false)} className="block py-2 text-white/90 font-medium hover:text-white">
            Contact
          </Link>
          <Link
            to="/login"
            onClick={() => setMobileOpen(false)}
            className="block text-center rounded-md text-sm font-semibold px-3 py-2 mt-2"
            style={{ background: `linear-gradient(90deg, ${BRAND.goldStart}, ${BRAND.goldEnd})`, color: '#1f2937' }}
          >
            {lang === 'fr' ? 'Se connecter' : 'Sign In'}
          </Link>
        </div>
      )}
    </header>
  );
}
